import { useState, useEffect } from 'react';
import { Stack, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { CustomTextfield } from '@/components/CustomTextfield/styles';
import { DeleteButton, LeftSide, SettingBox, WhiteWrapper } from '../styles';
import theme from '@/theme';
import { useAuthStore } from '@/stores/authStore';
import {
  doReauthenticateWithPassword,
  doUpdatePassword,
  hasPasswordProvider,
  doLinkWithPassword,
} from '@/firebase/auth';
import useAlertStore from '@/stores/alertStore';
import { FIREBASE_ERRORS } from '@/helpers/errors';
import { asApiError } from '@/utils/apiErrorMessage';

const ResetPassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);

  const { currentUser } = useAuthStore();
  const { showAlert } = useAlertStore();

  useEffect(() => {
    // Check if user has password provider on component mount and when user changes
    setHasPassword(hasPasswordProvider());
  }, [currentUser]);

  const validatePasswords = () => {
    // Only check current password if user already has one
    if (hasPassword && !currentPassword) {
      showAlert('Please enter your current password', 'error');
      return false;
    }

    if (!newPassword) {
      showAlert('Please enter a password', 'error');
      return false;
    }

    if (newPassword.length < 6) {
      showAlert('Password must be at least 6 characters long', 'error');
      return false;
    }

    if (newPassword !== confirmPassword) {
      showAlert('Passwords do not match', 'error');
      return false;
    }

    // Only check if passwords are different if user has existing password
    if (hasPassword && currentPassword === newPassword) {
      showAlert('New password must be different from current password', 'error');
      return false;
    }

    return true;
  };

  const handlePasswordUpdate = async () => {
    if (!currentUser || !currentUser.email) {
      showAlert('No user is currently logged in', 'error');
      return;
    }

    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);

    try {
      if (hasPassword) {
        // User has a password, so we need to reauthenticate first
        await doReauthenticateWithPassword(currentUser.email, currentPassword);
        await doUpdatePassword(newPassword);
        showAlert('Password updated successfully!', 'success');
      } else {
        // User doesn't have a password (Google auth), so we link a password
        await doLinkWithPassword(newPassword);
        showAlert('Password set successfully! You can now sign in with your email and password.', 'success');
        setHasPassword(true); // Update state to reflect new password
      }

      // Clear the form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (caught: unknown) {
      const error = asApiError(caught);
      if (
        error.code === FIREBASE_ERRORS.AUTH_WRONG_PASSWORD ||
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/invalid-credential'
      ) {
        showAlert('Current password is incorrect', 'error');
      } else if (
        error.code === FIREBASE_ERRORS.AUTH_REQUIRES_RECENT_LOGIN ||
        error.code === 'auth/requires-recent-login'
      ) {
        showAlert('For security reasons, please sign out and sign in again before changing your password', 'error');
      } else if (error.code === 'auth/weak-password') {
        showAlert('Password is too weak. Please use a stronger password', 'error');
      } else if (error.code === 'auth/email-already-in-use' || error.code === 'auth/provider-already-linked') {
        showAlert('A password has already been set for this account', 'error');
        setHasPassword(true);
      } else {
        showAlert('Failed to update password. Please try again.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = (field: 'current' | 'new' | 'confirm') => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
    }
  };

  return (
    <SettingBox>
      <LeftSide>
        <Typography variant="body1" fontWeight="700">
          {hasPassword ? 'Change Password' : 'Set Password'}
        </Typography>
      </LeftSide>

      <WhiteWrapper gap="1.5rem">
        <Typography variant="body2" color={theme.palette.neutral.greyFont}>
          {hasPassword
            ? "Update your password to keep your account secure. You'll need to enter your current password for verification, then create a new password."
            : "You signed in with Google and don't have a password yet. Set a password to enable email/password sign-in alongside your Google authentication."}
        </Typography>

        <Stack gap="1rem">
          {hasPassword && (
            <Stack gap="0.5rem">
              <Typography variant="body2" fontWeight="700">
                Current Password
              </Typography>
              <CustomTextfield
                type={showCurrentPassword ? 'text' : 'password'}
                variant="outlined"
                size="small"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => handleClickShowPassword('current')} edge="end" size="small">
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>
          )}

          <Stack gap="0.5rem">
            <Typography variant="body2" fontWeight="700">
              {hasPassword ? 'New Password' : 'Password'}
            </Typography>
            <CustomTextfield
              type={showNewPassword ? 'text' : 'password'}
              variant="outlined"
              size="small"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={
                hasPassword ? 'Enter your new password (min. 6 characters)' : 'Create a password (min. 6 characters)'
              }
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleClickShowPassword('new')} edge="end" size="small">
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          <Stack gap="0.5rem">
            <Typography variant="body2" fontWeight="700">
              {hasPassword ? 'Confirm New Password' : 'Confirm Password'}
            </Typography>
            <CustomTextfield
              type={showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              size="small"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={hasPassword ? 'Re-enter your new password' : 'Re-enter your password'}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleClickShowPassword('confirm')} edge="end" size="small">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>
        </Stack>

        <DeleteButton variant="outlined" color="error" onClick={handlePasswordUpdate} disabled={isLoading}>
          {isLoading ? (hasPassword ? 'Updating...' : 'Setting...') : hasPassword ? 'Update Password' : 'Set Password'}
        </DeleteButton>
      </WhiteWrapper>
    </SettingBox>
  );
};

export default ResetPassword;
